package com.sddamico.draftmachine.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.components.widgets.CodeInput
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.TransitionRole
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.sddamico.draftmachine.ds.motion.enterOnMount
import com.sddamico.draftmachine.ds.motion.shakeOn
import com.sddamico.draftmachine.ds.motion.transitionRole
import com.sddamico.draftmachine.ds.roleH1
import com.sddamico.draftmachine.ds.roleMono
import com.sddamico.draftmachine.ds.roleSmall
import com.sddamico.draftmachine.models.MeResponse
import com.sddamico.draftmachine.models.SendOtpRequest
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.models.VerifyRequest
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.transitionTo
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

private enum class LoginStep { Email, Code }

private const val RESEND_COOLDOWN_SECONDS = 60

@Page
@Composable
fun LoginPage() {
  val ctx = rememberPageContext()
  val scope = rememberCoroutineScope()

  var step by remember { mutableStateOf(LoginStep.Email) }
  var email by remember { mutableStateOf("") }
  var code by remember { mutableStateOf("") }
  var sending by remember { mutableStateOf(false) }
  var verifying by remember { mutableStateOf(false) }
  var emailError by remember { mutableStateOf<String?>(null) }
  var codeError by remember { mutableStateOf<String?>(null) }
  var secondsLeft by remember { mutableStateOf(0) }
  var countdownKey by remember { mutableStateOf(0) }
  var codeRejections by remember { mutableStateOf(0) }
  val returnTo = remember {
    window.location.search.removePrefix("?").split("&")
      .firstOrNull { it.startsWith("returnTo=") }?.substringAfter("returnTo=")
      ?.takeIf { it.matches(Regex("^/draft/[0-9a-fA-F-]{36}$")) }
  }

  // Drive the resend countdown while on the code step; restarts whenever [countdownKey] changes.
  LaunchedEffect(countdownKey) {
    if (countdownKey == 0) return@LaunchedEffect
    secondsLeft = RESEND_COOLDOWN_SECONDS
    while (secondsLeft > 0) {
      delay(1000)
      secondsLeft--
    }
  }

  fun sendCode(onSent: () -> Unit) {
    scope.launch {
      try {
        apiPost(
          "/api/auth/send-otp",
          apiJson.encodeToString(SendOtpRequest.serializer(), SendOtpRequest(email.trim())),
          SuccessResponse.serializer(),
        )
        onSent()
      } catch (e: ApiException) {
        emailError = e.errorMessage
      } catch (e: Throwable) {
        emailError = e.message ?: UNREACHABLE_MESSAGE
      } finally {
        sending = false
      }
    }
  }

  fun submitEmail() {
    if (email.isBlank() || sending) return
    sending = true
    emailError = null
    sendCode {
      ViewTransitions.run(Motion.Forward) {
        code = ""
        codeError = null
        step = LoginStep.Code
      }
      countdownKey++
    }
  }

  fun backToEmail() {
    ViewTransitions.run(Motion.Back) {
      step = LoginStep.Email
      code = ""
      codeError = null
    }
  }

  fun resend() {
    if (secondsLeft > 0) return
    codeError = null
    code = ""
    sendCode { countdownKey++ }
  }

  fun verify(submitCode: String) {
    if (submitCode.length < 6 || verifying) return
    verifying = true
    codeError = null
    scope.launch {
      try {
        apiPost(
          "/api/auth/verify",
          apiJson.encodeToString(VerifyRequest.serializer(), VerifyRequest(email.trim(), submitCode)),
          MeResponse.serializer(),
        )
        ctx.router.transitionTo(returnTo ?: "/", motion = Motion.Forward)
      } catch (e: ApiException) {
        codeError = e.errorMessage
        codeRejections++
      } catch (e: Throwable) {
        codeError = e.message ?: UNREACHABLE_MESSAGE
        codeRejections++
      } finally {
        verifying = false
      }
    }
  }

  when (step) {
    LoginStep.Email -> LoginScaffold(
      stepLabel = "Log in · Step 1 of 2",
      onBack = { ctx.router.transitionTo("/") },
    ) {
      SpanText("Where can the machine reach you?", Modifier.roleH1().fontSize(26.px).displayBlock())
      SpanText(
        "Email runs the whole draft — your pick links land in this inbox.",
        Modifier.roleSmall().color(DmColor.textMuted).lineHeight(1.55).displayBlock(),
      )
      Input(
        label = "Email",
        icon = "envelope-simple",
        type = "email",
        placeholder = "you@example.com",
        value = email,
        error = emailError,
        onValueChange = { email = it; emailError = null },
        onEnter = { submitEmail() },
      )
      Button(
        "Send the code", variant = ButtonVariant.Primary, size = ButtonSize.Lg,
        iconRight = "arrow-right", fullWidth = true, loading = sending,
        disabled = email.isBlank(), onClick = { submitEmail() },
      )
      Div(attrs = Modifier.fillMaxWidth().textAlign(TextAlign.Center).toAttrs()) {
        SpanText("6-digit code · expires in 10 minutes", Modifier.dmStamp().color(DmColor.steel500))
      }
    }

    LoginStep.Code -> LoginScaffold(
      stepLabel = "Log in · Step 2 of 2",
      onBack = { backToEmail() },
    ) {
      SpanText("Punch in the code", Modifier.roleH1().fontSize(26.px).displayBlock())
      SentToLine(email = email, onWrongAddress = { backToEmail() })
      CodeInput(
        value = code,
        error = codeError != null,
        modifier = Modifier.shakeOn(codeRejections),
        onValueChange = { code = it; if (codeError != null) codeError = null },
        onComplete = { verify(it) },
      )
      if (codeError != null) {
        Div(
          attrs = Modifier.displayFlex().gap(8.px).alignItems(AlignItems.FlexStart)
            .color(DmColor.rust300).enterOnMount().toAttrs()
        ) {
          Icon("warning", IconWeight.Bold, Modifier.fontSize(16.px).margin(top = 1.px).flex("0 0 auto"))
          SpanText(codeError!!, Modifier.roleSmall())
        }
      }
      Button(
        if (codeError != null) "Try again" else "Verify & log in",
        variant = ButtonVariant.Primary, size = ButtonSize.Lg, icon = "key",
        fullWidth = true, loading = verifying, disabled = codeError == null && code.length < 6,
        onClick = { if (codeError != null) { code = ""; codeError = null } else verify(code) },
      )
      ResendRow(secondsLeft = secondsLeft, onResend = { resend() })
    }
  }
}

/** The mobile login shell — dark grid backdrop, a stepper header with a back caret, a content column. */
@Composable
private fun LoginScaffold(
  stepLabel: String,
  onBack: () -> Unit,
  content: @Composable () -> Unit,
) {
  MobileFrame(
    header = {
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px)
          .height(56.px).padding(leftRight = 16.px)
          .borderBottom(1.px, LineStyle.Solid, DmColor.border)
          .transitionRole(TransitionRole.Header).toAttrs()
      ) {
        IconButton(
          icon = "caret-left",
          label = "Back",
          variant = ButtonVariant.Ghost,
          size = ButtonSize.Sm,
          modifier = Modifier.width(24.px).height(24.px)
            .backgroundColor(Colors.Transparent).boxShadowRaw("none"),
          onClick = onBack,
        )
        SpanText(stepLabel, Modifier.dmStamp().color(DmColor.textMuted))
      }
    },
  ) {
    Div(
      attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(16.px)
        .padding(top = 28.px, leftRight = 20.px, bottom = 28.px).toAttrs()
    ) { content() }
  }
}

/** "Sent to <email> · Wrong address?" helper line. */
@Composable
private fun SentToLine(email: String, onWrongAddress: () -> Unit) {
  Div(attrs = Modifier.roleSmall().color(DmColor.textMuted).lineHeight(1.55).toAttrs()) {
    SpanText("Sent to ", Modifier.roleSmall().color(DmColor.textMuted))
    SpanText(email, Modifier.roleMono().color(DmColor.textSecondary))
    SpanText(" · ", Modifier.roleSmall().color(DmColor.textMuted))
    SpanText(
      "Wrong address?",
      Modifier.roleSmall().fontWeight(DmType.weightSemibold).color(DmColor.brass400)
        .cursor(Cursor.Pointer).onClick { onWrongAddress() },
    )
  }
}

/** Resend-code row — isolated so its 1Hz countdown tick only recomposes this leaf. */
@Composable
private fun ResendRow(secondsLeft: Int, onResend: () -> Unit) {
  Div(attrs = Modifier.fillMaxWidth().textAlign(TextAlign.Center).toAttrs()) {
    if (secondsLeft > 0) {
      SpanText(
        "Resend in ${secondsLeft / 60}:${(secondsLeft % 60).toString().padStart(2, '0')}",
        Modifier.dmStamp().color(DmColor.steel500),
      )
    } else {
      SpanText(
        "Resend the code",
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).fontWeight(DmType.weightBold)
          .letterSpacing(DmType.trackingWider).textTransform(
            com.varabyte.kobweb.compose.css.TextTransform.Uppercase
          )
          .color(DmColor.brass400).cursor(Cursor.Pointer).onClick { onResend() },
      )
    }
  }
}
